// @ts-nocheck
/** @import {SelfieStepupProps} from './SelfieStepupTypes' */
/** @import {Handle} from 'onfido-sdk-ui/types/Onfido' */
/** @import {GetSelfieCheckResultResponse} from './Mutations/getSelfieCheckResultTypes' */

import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useMutation} from '@apollo/react-hooks';
import {print} from 'graphql/language/printer';

import Modal from '../Modal/Modal';
import Button from '../Form/Button/Button';
import Loading from '../Loading/Loading';

import mutations from './Mutations/mutations';
import {mountOnfido} from './Onfido';
import {usePolling} from '../../utils/hooks';
import {useCurrentUser} from '../../contexts/CurrentUserProvider';

import './selfieStepup.scss';
import {useLogger} from "../../contexts/Logger";


function SelfieStepup(/** @type {SelfieStepupProps} */ props) {
    const [createApplicantAndToken] = useMutation(mutations.CREATE_APPLICANT_AND_TOKEN);
    const [saveSelfieId] = useMutation(mutations.SAVE_SELFIE_ID);

    const [step, setStep] = useState(1);
    const [token, setToken] = useState('');
    const [provider, setProvider] = useState('');
    const [selfieFormWebUrl, setSelfieFormWebUrl] = useState('');
    const [checkData, setCheckData] = useState('');
    const [pollForResult, setPollForResult] = useState(false);
    const [pollForUpdate, setPollForUpdate] = useState(false);
    const [_result, setResult] = useState(/** @type {boolean} */ false);
    const [onfidoMount, setOnfidoMount] = useState(/** @type {null | Handle} */ (null));
    const [remountCount, setRemountCount] = useState(0);
    const jumioSdkRef = useRef(null);
    const [tryAgainText, setTryAgainText] = useState('Try again');
    const [tryAgainDisabled, setTryAgainDisabled] = useState(false);
    
    const logger = useLogger();
    const currentUser = useCurrentUser();
    
    // Define handlers before effects that use them
    const onVerificationFailure = () => {
        setStep(4);
    };
    
    const handleCheckResult = async (/** @type {GetSelfieCheckResultResponse} */ result) => {
        try {
            if (result && result.getSelfieCheckResult && result.getSelfieCheckResult.complete) {
                logger.info(`Selfie check completed, result: ${result.getSelfieCheckResult.success}`);

                // Stop polling now that we have a complete result
                setPollForResult(false);
                setPollForUpdate(false);
                setResult(result.getSelfieCheckResult.success);

                // blocked account
                if (result.getSelfieCheckResult.signOut) {
                    logger.info(`Force logging out`);

                    if (currentUser?.logout) currentUser.logout(`${window.location.origin}${props.onBlockedUrl}`);
                }

                // happy path
                if (result.getSelfieCheckResult.success) {
                    logger.info(`Selfie check passed successfully`);
                    try {
                        await props.onUserVerified(result.getSelfieCheckResult.flowId);
                        setStep(3);
                    } catch (verifyError) {
                        logger.error(`Failed to verify user`, {}, verifyError);
                        setPollForResult(false);
                        setPollForUpdate(false);
                        onVerificationFailure();
                    }
                } else {
                    logger.info(`Selfie check failed`);
                    onVerificationFailure();
                }
            } else {
                logger.debug(`Incomplete check result, continuing to poll`, {getSelfieCheckResult: result?.getSelfieCheckResult});
            }
        } catch (e) {
            logger.error(`Failed to check result`, {}, e);
            setPollForResult(false);
            setPollForUpdate(false);
        }
    };
    
    // Load Jumio SDK from public assets
    useEffect(() => {
        if (provider !== 'JUMIO') return;
        
        const loadJumioSdk = async () => {
            // Ensure CSS is loaded. Try to import the packaged CSS first so the
            // bundler can include it when available; fall back to the public copy.
            if (!document.querySelector('link[data-jumio-styles]')) {
                try {
                    await import('@jumio/websdk/assets/style.css');
                } catch (e) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = '/jumio/style.css';
                    link.setAttribute('data-jumio-styles', 'true');
                    document.head.appendChild(link);
                }
            }

            // Load script if not already loaded from public assets
            if (!window.customElements || !window.customElements.get('jumio-sdk')) {
                return new Promise(function (resolve) {
                    const script = document.createElement('script');
                    script.type = 'module';
                    script.src = '/jumio/index.js';
                    script.async = true;
                    script.onload = function () { resolve(); };
                    script.onerror = function () {
                        logger.error('Failed to load Jumio SDK');
                        resolve();
                    };
                    document.body.appendChild(script);
                });
            }
        };
        
        loadJumioSdk().then(() => {
            // Wait for element to render, then check if shadow DOM is ready
            const checkSdkReady = () => {
                const element = document.querySelector('jumio-sdk');
                if (element && element.shadowRoot && element.shadowRoot.innerHTML.length > 0) {
                    logger.info('Jumio SDK ready');
                    return true;
                }
                return false;
            };
            
            if (checkSdkReady()) {
                return;
            }
            
            // Use MutationObserver to detect when shadow DOM is populated
            const element = document.querySelector('jumio-sdk');
            if (element) {
                const observer = new MutationObserver(() => {
                    if (element.shadowRoot && element.shadowRoot.innerHTML.length > 0) {
                        logger.info('Jumio SDK shadow DOM ready');
                        observer.disconnect();
                    }
                });
                observer.observe(element, { subtree: true, childList: true });
            }
        });
    }, [provider, logger]);
    
    // Set up Jumio event listeners
    useEffect(() => {
        if (provider !== 'JUMIO' || step !== 1) return;
        
        const handleWorkflowSuccess = () => {
            logger.info('Jumio workflow completed successfully');
            // once the workflow has finished we still need to ask the backend for the
            // actual result, so start polling then show the "checking" screen
            setPollForResult(true);
            setStep(2);
            setPollForUpdate(false);
        };
        
        const handleWorkflowFailed = () => {
            logger.info('Jumio workflow failed');
            onVerificationFailure();
        };
        
        const handleWorkflowRetry = () => {
            logger.info('Jumio workflow retry triggered');
            setRemountCount(remountCount + 1);
        };
        
        // Wait a bit for the element to be available and rendered
        const attachListeners = setInterval(() => {
            const element = document.querySelector('jumio-sdk');
            if (element && element.shadowRoot && element.shadowRoot.innerHTML.length > 0) {
                clearInterval(attachListeners);
                jumioSdkRef.current = element;
                logger.info('Attaching Jumio event listeners');
                element.addEventListener('workflow:success', handleWorkflowSuccess);
                element.addEventListener('workflow:failed', handleWorkflowFailed);
                element.addEventListener('workflow:retry', handleWorkflowRetry);
            }
        }, 200);
        
        return () => {
            clearInterval(attachListeners);
            jumioSdkRef.current?.removeEventListener('workflow:success', handleWorkflowSuccess);
            jumioSdkRef.current?.removeEventListener('workflow:failed', handleWorkflowFailed);
            jumioSdkRef.current?.removeEventListener('workflow:retry', handleWorkflowRetry);
        };
    }, [provider, step, logger, remountCount, handleCheckResult, onVerificationFailure]);
    
    // controls initital mount when the visible prop is passed in
    useEffect(() => {
        if (props.visible && provider !== 'JUMIO') {
            mount();
        }
    }, [props.visible, provider]);

    // used to remount onfido - since setState is async we need to guarentee that the mount div is there
    useEffect(() => {
        if (remountCount === 0 || step !== 1)
            return;

        teardown();
        mount();
    }, [remountCount, step]);

    usePolling(async () => {
        if (!pollForUpdate)
            return;

        logger.debug('Step1 polling for update');

        try {
            const accessToken = currentUser?.accessToken;

            // had to use fetch instead of the useQuery hook as it can only be used at root component level
            const result = await fetch('/graphql', {

                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    query: print(mutations.GET_SELFIE_CHECK_UPDATE),
                    variables: {
                        request: {
                            checkData: ""
                        }
                    }
                }),
            });

            if (result.status === 200) {
                const resultBody = await result.json();
                
                // Check for GraphQL errors
                if (resultBody.errors) {
                    logger.error(`GraphQL error in GET_SELFIE_CHECK_UPDATE`, {errors: resultBody.errors});
                    return;
                }
                
                if (resultBody.data?.getSelfieCheckUpdate?.selfieCaptured) {
                    logger.info('Selfie captured detected in polling, transitioning to step 2');
                    setPollForUpdate(false);
                    setStep(2);
                    setPollForResult(true);
                }
            } else {
                logger.error(`Failed to fetch selfie check update`, {status: result.status});
            }

        } catch (e) {
            logger.error(`Failed to poll for selfie check update`, {}, e);
        }
    }, [pollForUpdate, logger], 5000);

    const init = async () => {
        const result = await createApplicantAndToken({
            variables: {
                request: {
                    context: props.context
                }
            }
        });

        if (result.data.createApplicantAndToken) 
        {
            setProvider(result.data.createApplicantAndToken.provider || "ONFIDO");
            setCheckData(result.data.createApplicantAndToken.checkData);
            
            if (result.data.createApplicantAndToken.provider && result.data.createApplicantAndToken.provider === "JUMIO") {
                // For JUMIO: get token for SDK and optional webFormUrl
                setToken(result.data.createApplicantAndToken.token);
                if (result.data.createApplicantAndToken.selfieFormWebUrl) {
                    setSelfieFormWebUrl(result.data.createApplicantAndToken.selfieFormWebUrl);
                }
                setPollForUpdate(true);
            } else {
                // For ONFIDO: get token for Onfido SDK
                setToken(result.data.createApplicantAndToken.token);
            }
        }
    };

    const mount = () => {
        setOnfidoMount(
            mountOnfido(
                props.mobileNumber,
                token,
                saveFaceId));
    };

    // todo might need retry / failure logic
    const saveFaceId = async (/** @type {unknown} */ faceId) => {
        await saveSelfieId({
            variables: {
                request: {
                    id: faceId,
                    checkData: checkData
                }
            }
        });

        setStep(2);
        setPollForResult(true);
    };

    // pass init back to be called by parent
    if (props.setInit && typeof props.setInit === "function") {
        props.setInit(init);
    }

    const onSuccessClose = () => {
        props.onComplete();
        setStep(1);
        setPollForResult(false);
        setPollForUpdate(false);
        teardown();
    };

    const onCancel = () => {
        props.onCancel();
        setStep(1);
        setPollForResult(false);
        setPollForUpdate(false);
        teardown();
    };

    const teardown = () => {
        if (onfidoMount) {
            onfidoMount.tearDown();
            setOnfidoMount(null);
        }
    };

    const tryAgainClick = async () => {
        if (provider === 'JUMIO') {
            // Jumio doesn't allow multiple attempts within the same workflow, so we need to remount the SDK which will trigger a new workflow with a new attempt
            logger.info(`Try again clicked for Jumio, remounting SDK, remount count ${remountCount}`);
            
            setTryAgainText('Reloading...');
            setTryAgainDisabled(true);
            setPollForResult(false);
            setPollForUpdate(false);
            await init();
            setStep(1);
            setTryAgainText('Try again');
            setTryAgainDisabled(false);

        } else {
            logger.info(`Try again clicked, remount count ${remountCount}`);
            setStep(1);
            setRemountCount(remountCount + 1);
        }
    };

    const renderStep = () => {
        if (step === 1) {
            return <StepOne title={props.title} provider={provider} webUrl={selfieFormWebUrl} token={token}/>
        } else if (step === 2) {
            return <StepTwo checkData={checkData} poll={pollForResult} resultCallback={handleCheckResult}
                            title={props.title}/>
        } else if (step === 3) {
            return <StepThree successCopy={props.successCopy} onButton={onSuccessClose}/>
        } else if (step === 4) {
            return <StepFour failureCopy={props.failureCopy} onButton={tryAgainClick} tryAgainText={tryAgainText} tryAgainDisabled={tryAgainDisabled}/>
        }

        return <></>;
    }

    const getModalClassName = () => {
        if (provider === 'JUMIO' && step === 3) {
            return "modal modal--fixed-height modal--wide modal__mobile-optimised modal-size";
        }
        if (provider === 'JUMIO' && step === 2) {
            return "modal modal--fixed-height modal--wide modal__mobile-optimised modal-size";
        }
        if (provider === 'JUMIO') {
            return "modal modal--full-height modal--wide modal__mobile-optimised modal-size";
        }
        return "modal modal--fixed-height modal--wide modal__mobile-optimised modal-size";
    };

    return (
        <>
            {props.visible && provider === 'JUMIO' &&
                <Modal className={getModalClassName()}
                       onClose={onCancel}
                       mobileOptimised>
                    {renderStep()}
                </Modal>
            }
            {props.visible && provider !== 'JUMIO' &&
                <Modal className="modal modal--fixed-height modal--wide modal__mobile-optimised modal-size"
                       onClose={onCancel}
                       mobileOptimised>
                    {renderStep()}
                </Modal>
            }
        </>
    );
}

function StepOne(/** @type {{ title: string; provider: string; webUrl: string; token: string }} */ props) {
    const dc = 'eu'; // 'eu' | 'us' | 'sgp'
    const locale = 'en';
    
    if (props.provider === "JUMIO") {
        return (
            <>
                <h2 className="modal__title">{props.title}</h2>
                <div id="jumio-mount" style={{ minHeight: '600px' }}>
                    {/* Templates used by the Jumio WebSDK to customise screens */}
                    <template id="jumio-cross-device-title">
                        <h1>Scan QR code</h1>
                    </template>
                    <template id="jumio-cross-device-subtitle">
                    </template>
                    <template id="jumio-cross-device-backward-button">
                    </template>
                    <template id="jumio-finish-success">
                        Thank you for verifiying, you can now continue with your application.
                    </template>

                    {props.token ? (
                        <jumio-sdk
                            token={props.token}
                            dc={dc}
                            locale={locale}
                            show-back-button="false"
                            style={{ minHeight: '600px' }}
                        >
                            <x-ds-color-scheme-config slot="theme" data-color="light"></x-ds-color-scheme-config>
                        </jumio-sdk>
                    ) : (
                        <p>Loading verification...</p>
                    )}
                </div>
            </>
        );
    }
    return (
        <>
            <h2 className="modal__title">{props.title}</h2>
            <div className='onfido-header'/>
            <div id="onfido-mount"></div>
        </>
    );
}

function StepTwo(/** @type {{ checkData: string; poll: boolean; resultCallback: (result: GetSelfieCheckResultResponse) => Promise<void>; title: string; }} */ props) {
    const currentUser = useCurrentUser();
    const logger = useLogger();

    usePolling(async () => {
        if (!props.poll) {
            return;
        }

        logger.debug('StepTwo polling tick', {checkData: props.checkData});

        try {
            const accessToken = currentUser?.accessToken;

            // had to use fetch instead of the useQuery hook as it can only be used at root component level
            const result = await fetch('/graphql', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    query: print(mutations.GET_SELFIE_CHECK_RESULT),
                    variables: {
                        request: {
                            checkData: props.checkData
                        }
                    }
                }),
            });

            if (result.status === 200) {
                const resultBody = await result.json();
                
                // Check for GraphQL errors in response
                if (resultBody.errors) {
                    logger.error(`GraphQL error in GET_SELFIE_CHECK_RESULT`, {errors: resultBody.errors});
                    return;
                }
                
                // Only call callback if we have valid data
                if (resultBody.data) {
                    props.resultCallback(resultBody.data);
                }
            } else {
                logger.error(`Failed to fetch selfie check result`, {status: result.status});
            }
        } catch (e) {
            logger.error(`Failed to poll selfie check result`, {}, e);
        }
    }, [props.poll, logger], 1000);

    return (
        <div className="selfie-stepup__verification-container">
            <h2 className="modal__title login__title otp-modal__title">{props.title}</h2>
            <p>Please wait whilst we verify your identity</p>
            <div className="selfie-stepup__loader-wrapper">
                <Loading />
            </div>
        </div>
    );
}

function StepThree(/** @type {{ successCopy: { title: string; body: string; }; onButton: () => void; }} */ props) {
    return (
        <div>
            <h4 className="modal__success-title">{props.successCopy.title}</h4>
            <p className="modal__paragraph" dangerouslySetInnerHTML={{__html: props.successCopy.body}}></p>
            <div className="modal__spacer"/>
            <Button size='med' customClassNames={["modal__button", "complete-button", "modal__button--left"]}
                    label="Done" onClick={props.onButton}/>
        </div>
    );
}

function StepFour(/** @type {{ failureCopy: { title: string; bodyFirstParagraph: string; }; onButton: () => void; }} */ props) {
    return <div>
        <h4 className="modal__success-title">{props.failureCopy.title}</h4>
        <p className="modal__paragraph" dangerouslySetInnerHTML={{__html: props.failureCopy.bodyFirstParagraph}}></p>
        <ul className="modal__list">
            <li>The photo of your face (selfie) isn't clear</li>
            <li>Your face is obscured by hair or glasses</li>
            <li>You're not looking straight ahead with a neutral expression</li>
            <li>A printed photo or photo of a digital screen has been used</li>
        </ul>
        <p className="modal__paragraph">
            Please click below to try again. For your security, after three unsuccessful attempts we’ll lock your
            account.
        </p>
        <div className="modal__spacer"/>
        <Button size='med' customClassNames={["modal__button", "complete-button", "modal__button--left"]}
                label={props.tryAgainText} onClick={props.onButton} disabled={props.tryAgainDisabled} />
    </div>
}

export default SelfieStepup;